import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { buttonClasses, type ButtonStyleOptions } from './Button';
import { cx, type Hue } from './shared';

export interface DropdownProps {
  /** Trigger content; rendered as a .btn with a caret. */
  label: ReactNode;
  /** Trigger button styling (hue, variant, size, …). Default: ghost slate. */
  trigger?: ButtonStyleOptions;
  /** Align the menu to the trigger's right edge. */
  end?: boolean;
  /** Open upward. */
  up?: boolean;
  /** Menu as wide as the trigger; trigger full width. */
  block?: boolean;
  /** Menu contents: DropdownItem, DropdownHeading, DropdownDivider. */
  children: ReactNode;
  className?: string;
}

/** A button that opens a menu. Closes on outside click, Escape, or choosing an item. */
export function Dropdown({ label, trigger = { variant: 'ghost', hue: 'slate' }, end, up, block, children, className }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        ref.current?.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Arrow keys move between items.
  const onMenuKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = [...(ref.current?.querySelectorAll<HTMLElement>('.dropdown__item:not(:disabled)') ?? [])];
    const index = items.indexOf(document.activeElement as HTMLElement);
    items[(index + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
  };

  return (
    <div
      ref={ref}
      className={cx('dropdown', open && 'is-open', end && 'dropdown--end', up && 'dropdown--up', block && 'dropdown--block', className)}
    >
      <button
        type="button"
        className={buttonClasses({ ...trigger, block })}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <span className="dropdown__caret" aria-hidden="true" />
      </button>
      <div
        id={menuId}
        className="dropdown__menu"
        role="menu"
        onKeyDown={onMenuKey}
        // Choosing an item closes the menu.
        onClick={(e) => (e.target as HTMLElement).closest('.dropdown__item') && setOpen(false)}
      >
        {children}
      </div>
    </div>
  );
}

export interface DropdownItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  /** Right-aligned hint, e.g. a shortcut or count. */
  meta?: ReactNode;
  selected?: boolean;
  /** e.g. 'red' for a destructive action. */
  hue?: Hue;
}

export function DropdownItem({ icon, meta, selected, hue, className, children, ...rest }: DropdownItemProps) {
  return (
    <button
      type="button"
      role={selected === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={selected}
      className={cx('dropdown__item', hue && `dropdown__item--${hue}`, selected && 'is-selected', className)}
      {...rest}
    >
      {icon && <span className="dropdown__icon">{icon}</span>}
      {children}
      {meta && <span className="dropdown__meta">{meta}</span>}
    </button>
  );
}

export function DropdownHeading({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('dropdown__heading', className)} role="presentation" {...rest} />;
}

export function DropdownDivider() {
  return <hr className="dropdown__divider" />;
}
